import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HCExporting from 'highcharts/modules/exporting'
import HCOfflineExport from 'highcharts/modules/offline-exporting'
import HCBrokenAxis from 'highcharts/modules/broken-axis'
import drilldown from 'highcharts/modules/drilldown.js'
import { useEffect, useRef, useState } from 'react'
import {
  barChartSeparators,
  getAbbrev,
  texts
} from '@constants/marktstatistiken'
import { localizedDecimal } from '@utils'

if (typeof Highcharts === 'object') {
  HCExporting(Highcharts)
  HCOfflineExport(Highcharts)
  HCBrokenAxis(Highcharts)
  drilldown(Highcharts)

  Highcharts.wrap(
    Highcharts.Axis.prototype,
    'getLinePath',
    function (proceed, lineWidth) {
      const axis = this
      const brokenAxis = axis.brokenAxis
      const path = proceed.call(this, lineWidth)
      const start = path[0]

      let x = start[1]
      let y = start[2]

      ;(brokenAxis.breakArray || []).forEach(function (brk) {
        if (axis.horiz) {
          x = axis.toPixels(brk.from)
          path.splice(
            1,
            0,
            ['L', x - 4, y], // stop
            ['M', x - 9, y + 5],
            ['L', x + 1, y - 5], // left slanted line
            ['M', x - 1, y + 5],
            ['L', x + 9, y - 5], // higher slanted line
            ['M', x + 4, y]
          )
        } else {
          y = axis.toPixels(brk.from)
          path.splice(
            1,
            0,
            ['L', x, y - 4], // stop
            ['M', x + 5, y - 9],
            ['L', x - 5, y + 1], // lower slanted line
            ['M', x + 5, y - 1],
            ['L', x - 5, y + 9], // higher slanted line
            ['M', x, y + 4]
          )
        }
      })
      return path
    }
  )
}
/**
 * On top of each column, draw a zigzag line where the axis break is.
 */
function pointBreakColumn(e) {
  const point = e.point
  const brk = e.brk
  const shapeArgs = point.shapeArgs
  const x = shapeArgs.x
  const y = this.translate(brk.from, 0, 1, 0, 1)
  const w = shapeArgs.width
  const key = ['brk', brk.from, brk.to]
  const path = [
    'M',
    x,
    y,
    'L',
    x + w * 0.25,
    y + 4,
    'L',
    x + w * 0.75,
    y - 4,
    'L',
    x + w,
    y
  ]

  if (!point[key]) {
    point[key] = this.chart.renderer
      .path(path)
      .attr({
        'stroke-width': 2,
        stroke: point.series.options.borderColor
      })
      .add(point.graphic.parentGroup)
  } else {
    point[key].attr({
      d: path
    })
  }
}

/**
 * Remove the zigzag line after the column is no longer on the break.
 */
function pointOutsideOfBreak(e) {
  const point = e.point
  const brk = e.brk
  const key = ['brk', brk.from, brk.to]

  if (point[key]) {
    point[key].destroy()
    delete point[key]
  }
}

// es-lint-disable-next-line no-unused-vars
export const PieChart = ({
  chartRef,
  data,
  drilldownData,
  title,
  subtitle,
  updateTextColumns,
  seriesName,
  locale
}) => {
  const localeTexts = texts[locale] || texts.de

  const updateFlag = useRef(true)
  const [isSmallWindow, setIsSmallWindow] = useState(false)
  const [chartOptions, setChartOptions] = useState({
    isSmallWindow,
    chart: {
      type: 'pie',
      height: 560,
      marginBottom: 210,
      style: {
        fontFamily: 'CorporateS'
      },
      events: {
        render: function () {
          // TODO! Check if this is the right way to handle the fullscreen event...
          const chart = this
          if (chart.fullscreen.isOpen && updateFlag.current) {
            updateFlag.current = false
            setTimeout(
              () =>
                setChartOptions({
                  ...chartOptions,
                  legend: { floating: false, y: 0 }
                }),
              100
            )
            // updateFlag.current = true;
          } else if (!chart.fullscreen.isOpen && !updateFlag.current) {
            updateFlag.current = true
            setChartOptions({ ...chartOptions, legend: { floating: true } })
            /* chart.update({
                  legend: {
                    options: {floating: 'true'}
                  }
                }) */

            // updateFlag.current = true;
          }
        },
        drilldown: function (el) {
          const total = el.point.total
          const y = el.point.y
          updateTextColumns(
            localizedDecimal((y / 1000000000).toFixed(2), locale) +
              ' ' +
              localeTexts.billions,
            localizedDecimal(((y / total) * 100).toFixed(2), locale)
          ) // TODO: What if y is smaller than 1 Mrd.?
        },
        drillup: function (el) {
          if (el.target.drilldownLevels.length > 0) {
            const total = el.target.drilldownLevels[
              el.target.drilldownLevels.length - 1
            ].seriesOptions.data.reduce((a, e) => a + e.y, 0)
            const y =
              el.target.drilldownLevels[el.target.drilldownLevels.length - 1]
                .pointOptions.y
            updateTextColumns(
              (y / 1000000000).toFixed(2) + ' ' + localeTexts.billions,
              ((y / total) * 100).toFixed(2)
            ) // TODO: What if y is smaller than 1 Mrd.?
          } else {
            updateTextColumns(null, null, true)
          }
        }
      }
    },
    credits: {
      enabled: false
    },
    title: {
      text: '',
      align: 'left'
    },
    subtitle: {
      text: subtitle,
      align: 'left'
    },
    tooltip: {
      headerFormat: '',

      formatter: function (e /* eslint-disable-line no-unused-vars */) {
        return `<span style="color:${this.point.color}">\u25cf</span> ${this.point.name} (${localizedDecimal(this.point.percentage.toFixed(1), locale)}%)`
      }
    },
    accessibility: {
      announceNewData: {
        enabled: true
      },
      point: {
        valueSuffix: '%'
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        borderWidth: 2,
        cursor: 'pointer',
        innerSize: '60%',
        borderRadius: 8,
        dataLabels: {
          enabled: false
        }
      }
    },
    series: [
      {
        enableMouseTracking: true,
        showInLegend: true,
        animation: {
          duration: 2000
        },
        // colorByPoint: true,
        /* color: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, '#003399'],
          [1, '#3366AA']
        ]
      }, */
        name: seriesName,
        data
      }
    ],
    drilldown: {
      breadcrumbs: {
        buttonTheme: {
          states: {
            hover: {
              fill: 'rgba(181,90,48,1)',
              style: {
                color: 'white'
              }
            },
            select: {
              fill: 'rgba(181,90,48,0.7)',
              style: {
                color: 'white',
                fontWeight: 'lighter'
              }
            }
          },
          style: {
            color: 'white'
          },
          fill: 'rgba(181,90,48,0.7)',
          padding: 8,
          stroke: 'rgba(181,90,48,0.7)',
          'stroke-width': 0
        },
        separator: {
          text: '➤',
          style: {
            color: 'rgba(181,90,48,0.7)',
            fontSize: '22px'
          }
        },
        floating: false,
        position: {
          align: 'right'
        },
        useHTML: true,
        formatter: function (level) {
          const abbrev = getAbbrev(level.levelOptions.name, locale)
          if (isSmallWindow && abbrev) {
            return `${abbrev}`
          }
          return `${level.levelOptions.name}`
        },
        showFullPath: true
      },
      series: drilldownData
    },
    legend: {
      title: {
        text: '',
        style: {
          fontSize: '12px',
          letterSpacing: '0.3px',
          color: '#0A1944',
          fontWeight: '500'
        }
      },
      itemStyle: {
        fontSize: '12px',
        letterSpacing: '0.25px',
        color: '#0A1944',
        fontWeight: '500'
      },
      labelFormatter: function () {
        return (
          this.name +
          ' (' +
          localizedDecimal(this.percentage.toFixed(1), locale) +
          '%)'
        )
      },
      floating: true,
      useHTML: true,
      alignColumns: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'top',
      itemDistance: 11,
      itemMarginBottom: 1,
      itemMarginTop: 8,
      y: 350,
      x: 0
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    },
    exporting: {
      chartOptions: {
        title: {
          text: title,
          align: 'center'
        }
      },
      filename: title,
      fallbackToExportServer: false
    }
  })

  useEffect(() => {
    if (typeof window === 'object') {
      const handleResize = () => {
        setIsSmallWindow(window.innerWidth < 576)
      }

      handleResize()

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [])

  useEffect(() => {
    setChartOptions((prevState) => ({
      ...prevState,
      drilldown: {
        ...prevState.drilldown,
        breadcrumbs: {
          ...prevState.drilldown.breadcrumbs,
          formatter: function (level) {
            const abbrev = getAbbrev(level.levelOptions.name, locale)
            if (isSmallWindow && abbrev) {
              return `${abbrev}`
            }
            return `${level.levelOptions.name}`
          }
        }
      }
    }))
  }, [isSmallWindow])

  const afterChartCreated = function (chart) {
    if (!chartRef.current) {
      chartRef.current = chart
    }
  }

  return (
    <div style={{ height: 560 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        callback={afterChartCreated}
      />
    </div>
  )
}

export const BarChart = ({
  chartRef,
  xAxisCategories,
  seriesData,
  title,
  locale
}) => {
  const updateFlag = useRef(true)
  let yAxisData = []
  seriesData.forEach((e) => {
    yAxisData = yAxisData.concat(e.data)
  }) // [95.9, 98.5, 99.7, 101, 101.9, 105.5, 103, 107.2, 109.1, 111.4, 111.2, 113]

  const localeTexts = texts[locale] || texts.de
  const separators = barChartSeparators[locale] || barChartSeparators.de

  // const toBreakingPoint = Math.floor(Math.min(...yAxisData) * 0.95 / 10) * 10
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'column',
      height: 503,
      style: {
        fontFamily: 'CorporateS'
      },
      events: {
        render: function () {
          // TODO! Check if this is the right way to handle the fullscreen event...
          const chart = this
          if (chart.fullscreen.isOpen && updateFlag.current) {
            updateFlag.current = false
            setTimeout(
              () =>
                setChartOptions({
                  ...chartOptions,
                  legend: { floating: false, y: 0 }
                }),
              100
            )
            // updateFlag.current = true;
          } else if (!chart.fullscreen.isOpen && !updateFlag.current) {
            updateFlag.current = true
            setChartOptions({ ...chartOptions, legend: { floating: true } })
          }
        }
      }
    },
    title: {
      text: '',
      align: 'left'
    },
    subtitle: {
      align: 'left'
    },
    xAxis: {
      categories: xAxisCategories,
      crosshair: true,
      accessibility: {
        description: localeTexts.months
      }
    },
    yAxis: {
      min: 0,
      lineColor: 'lightgrey',
      lineWidth: 1,
      tickInterval: 20000000000,
      title: {
        text: localeTexts.barChartYAxisTitle
      },
      labels: {
        formatter: function () {
          if (this.value >= 100000000) {
            return localizedDecimal(
              (this.value / 1000000000).toFixed(0),
              locale
            )
          }
          return this.value
        }
      },
      /* breaks: [{
        from: 3,
        to: toBreakingPoint
      }], */
      events: {
        pointBreak: pointBreakColumn,
        pointOutsideOfBreak
      }
    },
    tooltip: {
      formatter: function (e /* eslint-disable-line no-unused-vars */) {
        return (
          this.series.name +
          ' (' +
          Highcharts.numberFormat(
            this.y / 1000000000,
            1,
            separators.decimal,
            separators.thousands
          ) +
          ' ' +
          localeTexts.billions +
          ')' +
          (this.series.name.includes('SRI')
            ? `<br/>${localeTexts.total} (${Highcharts.numberFormat(this.point.stackTotal / 1000000000, 1, separators.decimal, separators.thousands)} ${localeTexts.billions})`
            : '')
        )
      }
    },
    credits: {
      enabled: false
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        stacking: 'normal'
      }
    },
    series: seriesData,
    legend: {
      itemStyle: {
        fontSize: '12px',
        letterSpacing: '0.25px',
        fontWeight: '500',
        color: '#0A1944'
      },
      itemDistance: 11,
      itemMarginBottom: 1,
      itemMarginTop: 8,
      reversed: true
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    },
    exporting: {
      chartOptions: {
        title: {
          text: title,
          align: 'center'
        }
      },
      filename: title,
      fallbackToExportServer: false
    }
  })

  const afterChartCreated = function (chart) {
    if (!chartRef.current) {
      chartRef.current = chart
    }
  }

  return (
    <div style={{ height: 560 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        callback={afterChartCreated}
      />
    </div>
  )
}
